// part-1
import { OverflowList } from '@theme/overflow-list';
import VariantPicker from '@theme/variant-picker';
import { ProductComponent } from '@theme/view-event-elements';
import { debounce, yieldToMainThread } from '@theme/utilities';
import { StandardEvents } from '@shopify/events';

(() => {
  if (document.getElementById('cc-product-card-style')) return;

  const style = document.createElement('style');
  style.id = 'cc-product-card-style';

  style.textContent = `
    product-card .cc-image-fade-container {
      position: relative !important;
      overflow: hidden !important;
    }

    product-card .cc-image-fade-container .cc-first-image {
      position: relative !important;
      z-index: 1 !important;
      width: 100% !important;
      height: 100% !important;
      display: block !important;
    }

    product-card .cc-hover-second-image {
      position: absolute !important;
      inset: 0 !important;
      width: 100% !important;
      height: 100% !important;
      z-index: 20 !important;
      opacity: 0 !important;
      visibility: hidden !important;
      pointer-events: none !important;
      display: block !important;
    }

    product-card .cc-hover-second-image img {
      width: 100% !important;
      height: 100% !important;
      display: block !important;
      object-fit: cover !important;
      pointer-events: none !important;
    }

    product-card .cc-image-fade-container.cc-image-hover-active .cc-hover-second-image {
      opacity: 1 !important;
      visibility: visible !important;
      pointer-events: none !important;
    }

    product-card .cc-image-fade-container slideshow-arrows {
      position: absolute !important;
      z-index: 999 !important;
      pointer-events: auto !important;
    }

    product-card .cc-image-fade-container slideshow-arrows button {
      position: relative !important;
      z-index: 1000 !important;
      pointer-events: auto !important;
    }
  `;

  document.head.appendChild(style);
})();

export class ProductCardLink extends ProductComponent {
  get productTransitionEnabled() {
    return this.getAttribute('data-product-transition') === 'true';
  }

  get featuredMediaUrl() {
    return this.getAttribute('data-featured-media-url');
  }
}

if (!customElements.get('product-card-link')) {
  customElements.define('product-card-link', ProductCardLink);
}

export class ProductCard extends ProductCardLink {
  requiredRefs = ['productCardLink'];

  #originalProductUrl = '';
  #selectedProductUrl = '';
  #originalProductTitle = '';
  #selectedProductTitle = '';
  #selectedImageState = {
    src: '',
    srcset: ''
  };
  #connectedProductImages = [];
  #connectedProductImageIndex = 0;
  #currentImageMode = 'original';
  #connectedRequestController = null;
  #hoverRequestToken = 0;
  #currentHoveredLabel = null;
  #userSelectedVariant = false;

  get variantPicker() {
    return this.querySelector('swatches-variant-picker-component') ||
      this.querySelector('variant-picker');
  }

  #getSlideshow() {
    return this.refs?.slideshow ||
      this.querySelector('slideshow');
  }

  #getSlides() {
    const slideshow = this.#getSlideshow();

    if (!slideshow) return [];

    return Array.from(
      slideshow.querySelectorAll('slideshow-slide')
    );
  }

  #getFirstSlide() {
    return this.#getSlides()[0] || null;
  }

  #getSecondSlide() {
    return this.#getSlides()[1] || null;
  }

  #getFirstImage() {
    const slide = this.#getFirstSlide();

    return slide?.querySelector('img') ||
      this.querySelector(
        '.product-card__image img, slideshow img'
      );
  }

  #getSecondImage() {
    const slide = this.#getSecondSlide();

    return slide?.querySelector('img') || null;
  }

  #getSelectedSwatch() {
    const picker = this.variantPicker;

    if (!picker) return null;

    return picker.querySelector(
      'input[type="radio"]:checked'
    );
  }

  #getLabelFromInput(input) {
    if (!input) return null;

    return input.closest(
      '.variant-option__button-label'
    ) || input.closest('label');
  }

  #getInputFromClick(event) {
    const target =
      event.target instanceof Element
        ? event.target
        : null;

    if (!target) return null;

    if (
      target instanceof HTMLInputElement &&
      target.type === 'radio'
    ) {
      return target;
    }

    const label =
      target.closest(
        '.variant-option__button-label'
      ) ||
      target.closest('label');

    if (!label) return null;

    const input =
      label.querySelector(
        'input[type="radio"]'
      );

    if (input) return input;

    const forId =
      label.getAttribute('for');

    if (!forId) return null;

    const connectedInput =
      document.getElementById(forId);

    return connectedInput instanceof HTMLInputElement
      ? connectedInput
      : null;
  }

  #setSelectedInput(input) {
    if (!input) return;

    const picker = this.variantPicker;

    if (!picker) return;

    const radios =
      picker.querySelectorAll(
        `input[name="${CSS.escape(input.name)}"]`
      );

    radios.forEach((radio) => {
      const selected =
        radio === input;

      radio.checked = selected;

      radio.setAttribute(
        'aria-checked',
        selected ? 'true' : 'false'
      );

      if (selected) {
        radio.setAttribute(
          'data-current-checked',
          'true'
        );
      } else {
        radio.removeAttribute(
          'data-current-checked'
        );
      }
    });
  }

  #getCurrentGroupCode(input, label) {
    return (
      label?.getAttribute(
        'data-current-product-group-code'
      ) ||
      input?.getAttribute(
        'data-current-product-group-code'
      ) ||
      this.getAttribute(
        'data-product-group-code'
      ) ||
      ''
    ).trim();
  }

  #getConnectedGroupCode(input, label) {
    return (
      label?.getAttribute(
        'data-connected-product-group-code'
      ) ||
      input?.getAttribute(
        'data-connected-product-group-code'
      ) ||
      ''
    ).trim();
  }

#getConnectedUrl(input, label) {
  const optionType =
    label?.getAttribute('data-option-type') ||
    input?.getAttribute('data-option-type') ||
    '';

  const currentGroup =
    this.#getCurrentGroupCode(
      input,
      label
    ).trim().toLowerCase();

  const connectedGroup =
    this.#getConnectedGroupCode(
      input,
      label
    ).trim().toLowerCase();

  let url =
    label?.getAttribute(
      'data-connected-product-url'
    ) ||
    input?.getAttribute(
      'data-connected-product-url'
    ) ||
    '';

  if (!url && optionType === 'color') {
    url =
      label?.getAttribute(
        'data-color-product-url'
      ) ||
      input?.getAttribute(
        'data-color-product-url'
      ) ||
      '';
  }

  if (!url && optionType === 'gemstone') {
    url =
      label?.getAttribute(
        'data-gemstone-product-url'
      ) ||
      input?.getAttribute(
        'data-gemstone-product-url'
      ) ||
      '';
  }

  if (!url) return '';

  if (
    currentGroup &&
    connectedGroup &&
    currentGroup === connectedGroup
  ) {
    return url;
  }

  if (
    optionType === 'color' &&
    url
  ) {
    return url;
  }

  if (
    optionType === 'gemstone' &&
    url
  ) {
    return url;
  }

  return '';
}

  #getConnectedVariantId(input, label) {
    return (
      label?.getAttribute(
        'data-connected-product-variant-id'
      ) ||
      input?.getAttribute(
        'data-connected-product-variant-id'
      ) ||
      ''
    );
  }

  #getConnectedFeaturedImage(input, label) {
    return (
      label?.getAttribute(
        'data-connected-product-featured-image'
      ) ||
      input?.getAttribute(
        'data-connected-product-featured-image'
      ) ||
      ''
    );
  }

  #getVariantImage(input, label) {
    return (
      label?.getAttribute(
        'data-variant-image'
      ) ||
      input?.getAttribute(
        'data-variant-image'
      ) ||
      label?.getAttribute(
        'data-variant-featured-image'
      ) ||
      input?.getAttribute(
        'data-variant-featured-image'
      ) ||
      ''
    );
  }

  #getHoverImage(input, label) {
    return (
      label?.getAttribute(
        'data-hover-image'
      ) ||
      input?.getAttribute(
        'data-hover-image'
      ) ||
      ''
    );
  }

  #getVariantUrl(input, label) {
    return (
      input?.getAttribute(
        'data-variant-url'
      ) ||
      label?.getAttribute(
        'data-variant-url'
      ) ||
      ''
    );
  }

  #optimizeImageUrl(url) {
    if (!url) return '';

    try {
      const parsed =
        new URL(
          url,
          window.location.origin
        );

      parsed.searchParams.set(
        'width',
        '1200'
      );

      return parsed.toString();
    } catch {
      return url;
    }
  }

  #setImage(image, url) {
    if (!image || !url) return;

    const finalUrl = this.#optimizeImageUrl(url);

    // Remove responsive source so Shopify does not
    // immediately replace our variant image.
    image.removeAttribute('srcset');

    image.src = finalUrl;

    // Store selected variant image.
    image.dataset.ccSelectedImage = finalUrl;

    // Store original image for restore.
    image.dataset.ccVariantImage = finalUrl;

    // Make sure browser uses this image.
    image.setAttribute('fetchpriority', 'high');

    // Force image refresh.
    image.decode?.().catch(() => { });

    console.log('[CC] Variant image set:', finalUrl);
  }
  #saveOriginalHoverImage() {
    const slideshow = this.#getSlideshow();
    if (!slideshow) return;

    const images = slideshow.querySelectorAll('img');
    if (images.length < 2) return;

    const secondImage = images[1];

    if (
      !secondImage.dataset.ccOriginalHoverImage
    ) {
      secondImage.dataset.ccOriginalHoverImage =
        secondImage.currentSrc ||
        secondImage.src ||
        secondImage.getAttribute('data-src') ||
        '';
    }
  }
  #saveCurrentImageState() {
    const image =
      this.#getFirstImage();

    if (!image) return;

    this.#selectedImageState = {
      src:
        image.currentSrc ||
        image.src ||
        '',
      srcset:
        image.srcset ||
        ''
    };
  }

  #restoreSelectedImage() {
    const image =
      this.#getFirstImage();

    if (!image) return;

    if (!this.#selectedImageState.src) {
      return;
    }

    image.removeAttribute('srcset');
    image.src =
      this.#selectedImageState.src;

    if (
      this.#selectedImageState.srcset
    ) {
      image.srcset =
        this.#selectedImageState.srcset;
    }
  }

  #getProductImages(product, variantId = '') {
    if (!product) return [];

    const images = [];

    /*
    * IMPORTANT:
    * If a connected variant is selected,
    * its variant featured image MUST be image #1.
    */

    if (variantId && Array.isArray(product.variants)) {
      const variant = product.variants.find(
        (item) =>
          String(item.id) === String(variantId)
      );

      const variantImage =
        typeof variant?.featured_image === 'string'
          ? variant.featured_image
          : variant?.featured_image?.src || '';

      if (variantImage) {
        images.push(variantImage);
      }
    }
    if (Array.isArray(product.images)) {
      product.images.forEach((image) => {
        const url =
          typeof image === 'string'
            ? image
            : image?.src || '';

        if (
          url &&
          !images.includes(url)
        ) {
          images.push(url);
        }
      });
    }

    const featured =
      typeof product.featured_image === 'string'
        ? product.featured_image
        : product.featured_image?.src || '';

    if (
      featured &&
      !images.includes(featured)
    ) {
      images.push(featured);
    }

    return images;
  }

  // part-2
  async #loadProduct(url) {
    if (!url) return null;

    try {
      this.#connectedRequestController?.abort();

      this.#connectedRequestController =
        new AbortController();

      const parsed =
        new URL(
          url,
          window.location.origin
        );

      parsed.search = '';

      const jsonUrl =
        `${parsed.pathname.replace(/\/$/, '')}.js`;

      const response =
        await fetch(
          jsonUrl,
          {
            credentials: 'same-origin',
            headers: {
              Accept:
                'application/json'
            },
            signal:
              this.#connectedRequestController.signal
          }
        );

      if (!response.ok) {
        return null;
      }

      return await response.json();
    } catch (error) {
      if (
        error?.name ===
        'AbortError'
      ) {
        return null;
      }

      console.warn(
        '[CC] Connected product load error:',
        error
      );

      return null;
    } finally {
      this.#connectedRequestController =
        null;
    }
  }

  #applyImages(images) {
    if (
      !Array.isArray(images) ||
      !images.length
    ) {
      return;
    }

    const slides =
      this.#getSlides();

    if (!slides.length) {
      return;
    }

    slides.forEach(
      (slide, index) => {
        const image =
          slide.querySelector('img');

        if (!image) return;

        if (images[index]) {
          this.#setImage(
            image,
            images[index]
          );

          image.classList.add(
            'cc-connected-product-image'
          );

          slide.removeAttribute(
            'data-cc-connected-hidden'
          );
        } else {
          slide.setAttribute(
            'data-cc-connected-hidden',
            'true'
          );
        }
      }
    );

    this.#currentImageMode =
      'connected';

    this.#connectedProductImages =
      images;

    this.#connectedProductImageIndex =
      0;

    this.#refreshHoverImage();
  }

#setHoverImage(url) {
  const slideshow = this.#getSlideshow();
  if (!slideshow) return;

  slideshow.classList.add('cc-image-fade-container');

  let overlay = slideshow.querySelector('.cc-hover-second-image');

  if (!overlay) {
    overlay = document.createElement('div');
    overlay.className = 'cc-hover-second-image';
    overlay.setAttribute('aria-hidden', 'true');

    const image = document.createElement('img');
    image.alt = '';
    image.loading = 'lazy';
    image.decoding = 'async';

    overlay.appendChild(image);
    slideshow.appendChild(overlay);
  }

  overlay.style.pointerEvents = 'none';

  const image = overlay.querySelector('img');
  if (!image) return;

  if (!url) {
    image.removeAttribute('src');
    image.removeAttribute('srcset');
    overlay.classList.remove('has-image');
    slideshow.classList.remove('cc-image-hover-active');
    return;
  }

  image.removeAttribute('srcset');
  image.src = this.#optimizeImageUrl(url);
  overlay.classList.add('has-image');
}

#refreshHoverImage() {
  const selected = this.#getSelectedSwatch();

  const label = this.#getLabelFromInput(selected);

  const slideshow = this.#getSlideshow();

  if (!slideshow) return;

  const firstImage = this.#getFirstImage();

  if (!firstImage) return;

  slideshow.classList.add('cc-image-fade-container');

  firstImage.classList.add('cc-first-image');

  let hoverUrl = '';

  if (
    this.#currentImageMode === 'connected' &&
    this.#connectedProductImages.length > 1
  ) {
    hoverUrl = this.#connectedProductImages[1];
  } else if (selected) {
    hoverUrl = this.#getHoverImage(
      selected,
      label
    );
  } else {
    const secondImage =
      this.#getSecondImage();

    hoverUrl =
      secondImage?.currentSrc ||
      secondImage?.src ||
      secondImage?.getAttribute('data-src') ||
      '';
  }

  this.#setHoverImage(hoverUrl);
}

  #showSelectedVariantImage(
    input,
    label
  ) {
    if (!input) return;

    this.#currentImageMode =
      'variant';

    this.#connectedProductImages =
      [];

    const variantImage =
      this.#getVariantImage(
        input,
        label
      );

    const connectedFeatured =
      this.#getConnectedFeaturedImage(
        input,
        label
      );

    const firstImage =
      variantImage ||
      connectedFeatured ||
      '';

    if (firstImage) {
      this.#setImage(
        this.#getFirstImage(),
        firstImage
      );
    }

    const hoverImage =
      this.#getHoverImage(
        input,
        label
      );

    this.#setHoverImage(
      hoverImage
    );

    this.#saveCurrentImageState();
  }

  async #loadConnectedVariant(
    url,
    variantId
  ) {
    const requestToken =
      ++this.#hoverRequestToken;

    const product =
      await this.#loadProduct(
        url
      );

    if (
      requestToken !==
      this.#hoverRequestToken
    ) {
      return;
    }

    if (!product) {
      return;
    }

    const images =
      this.#getProductImages(
        product,
        variantId
      );

    if (!images.length) {
      return;
    }

    this.#connectedProductImages =
      images;

    this.#connectedProductImageIndex =
      0;

    this.#currentImageMode =
      'connected';

    this.#applyImages(
      images
    );

    const firstImage =
      images[0] || '';

    const secondImage =
      images[1] || '';

    if (firstImage) {
      this.#setImage(
        this.#getFirstImage(),
        firstImage
      );
    }

    this.#setHoverImage(
      secondImage
    );
  }

  #updateProductLinks(url) {
    if (!url) return;

    try {
      const absoluteUrl =
        new URL(
          url,
          window.location.origin
        ).toString();

      const links = [
        this.refs?.productCardLink,
        this.refs?.cardGalleryLink,
        this.refs?.productTitleLink
      ];

      links.forEach(
        link => {
          if (
            link instanceof
            HTMLAnchorElement
          ) {
            link.href =
              absoluteUrl;
          }
        }
      );

      this.#selectedProductUrl =
        absoluteUrl;
    } catch { }
  }

  #setProductTitle(title) {
    if (!title) return;

    const elements = [
      this.refs?.productTitle,
      this.querySelector(
        '.product-card__title'
      ),
      this.querySelector(
        '[data-product-title]'
      )
    ].filter(Boolean);

    elements.forEach(
      element => {
        element.textContent =
          title;
      }
    );

    this.#selectedProductTitle =
      title;
  }

  #keepSelectedState() {
    const selected =
      this.#getSelectedSwatch();

    if (!selected) return;

    selected.checked =
      true;

    selected.setAttribute(
      'aria-checked',
      'true'
    );

    selected.setAttribute(
      'data-current-checked',
      'true'
    );
  }

  async #selectVariant(
    input
  ) {
    if (!input) return;

    const label =
      this.#getLabelFromInput(
        input
      );

    this.#setSelectedInput(
      input
    );

    this.#hoverRequestToken++;

    this.#connectedRequestController?.abort();

    const connectedUrl =
      this.#getConnectedUrl(
        input,
        label
      );

    const connectedVariantId =
      this.#getConnectedVariantId(
        input,
        label
      );

    const variantUrl =
      this.#getVariantUrl(
        input,
        label
      );

    const finalUrl =
      connectedUrl ||
      variantUrl;

    if (finalUrl) {
      const url =
        connectedUrl &&
          connectedVariantId
          ? (
            connectedUrl.includes('?')
              ? `${connectedUrl}&variant=${connectedVariantId}`
              : `${connectedUrl}?variant=${connectedVariantId}`
          )
          : finalUrl;

      this.#updateProductLinks(
        url
      );
    }

    if (connectedUrl) {
      await this.#loadConnectedVariant(
        connectedUrl,
        connectedVariantId
      );
    } else {
      this.#showSelectedVariantImage(
        input,
        label
      );
    }

    this.#keepSelectedState();

    this.#refreshHoverImage();
  }
  // part-3
  #previewVariant = async (event) => {
    const target = event?.target instanceof Element ? event.target : null;
    if (!target) return;

    const label =
      target.closest('.variant-option__button-label') ||
      target.closest('label');

    if (!label) return;

    const input = label.querySelector('input[type="radio"]');
    if (!input) return;

    this.#currentHoveredLabel = label;

    const requestToken = ++this.#hoverRequestToken;

    const connectedUrl = this.#getConnectedUrl(input, label);
    const connectedVariantId = this.#getConnectedVariantId(input, label);

    const firstImage =
      this.#getVariantImage(input, label) ||
      this.#getConnectedFeaturedImage(input, label);

    const secondImage = this.#getHoverImage(input, label);

    if (connectedUrl) {
      if (firstImage) {
        this.#setImage(this.#getFirstImage(), firstImage);
      }

      this.#setHoverImage(secondImage || '');

      const product = await this.#loadProduct(connectedUrl);

      if (
        requestToken !== this.#hoverRequestToken ||
        this.#currentHoveredLabel !== label ||
        !product
      ) {
        return;
      }

      const images = this.#getProductImages(
        product,
        connectedVariantId
      );

      if (!images.length) return;

      this.#connectedProductImages = images;
      this.#connectedProductImageIndex = 0;
      this.#currentImageMode = 'connected';

      const image1 = firstImage || images[0] || '';
      const image2 = secondImage || images[1] || '';

      if (image1) {
        this.#setImage(
          this.#getFirstImage(),
          image1
        );
      }

      this.#setHoverImage(image2);
      return;
    }

    this.#currentImageMode = 'variant';
    this.#connectedProductImages = [];

    if (firstImage) {
      this.#setImage(
        this.#getFirstImage(),
        firstImage
      );
    }

    this.#setHoverImage(secondImage || '');
  };

#resetVariant = () => {
  this.#hoverRequestToken++;

  this.#currentHoveredLabel = null;

  this.#connectedRequestController?.abort();

  const selected =
    this.#getSelectedSwatch();

  if (selected) {
    const label =
      this.#getLabelFromInput(
        selected
      );

    const connectedUrl =
      this.#getConnectedUrl(
        selected,
        label
      );

    const variantImage =
      this.#getVariantImage(
        selected,
        label
      );

    if (connectedUrl) {
      this.#currentImageMode =
        'connected';

      const firstImage =
        this.#connectedProductImages[0] ||
        '';

      const secondImage =
        this.#connectedProductImages[1] ||
        '';

      if (firstImage) {
        this.#setImage(
          this.#getFirstImage(),
          firstImage
        );
      }

      this.#setHoverImage(
        secondImage
      );
    } else {
      this.#currentImageMode =
        'variant';

      if (variantImage) {
        this.#setImage(
          this.#getFirstImage(),
          variantImage
        );
      }

      this.#setHoverImage(
        this.#getHoverImage(
          selected,
          label
        )
      );
    }

    this.#refreshHoverImage();

    return;
  }

  this.#currentImageMode =
    'original';

  this.#connectedProductImages = [];

  this.#restoreSelectedImage();

  const secondImage =
    this.#getSecondImage();

  const secondImageUrl =
    secondImage?.currentSrc ||
    secondImage?.src ||
    secondImage?.getAttribute('data-src') ||
    '';

  this.#setHoverImage(
    secondImageUrl
  );

  this.#refreshHoverImage();
};
  #handleSwatchClick = async (event) => {
    const input =
      this.#getInputFromClick(event);

    if (!input) return;

    this.#userSelectedVariant = true;

    const name =
      input.name || '';

    if (
      !name.includes('-swatch') &&
      !input.closest('.variant-option')
    ) {
      return;
    }

    event.preventDefault();
    event.stopPropagation();

    this.#setSelectedInput(input);

    await this.#selectVariant(input);
  };

  #handleProductSelect = async (event) => {
    const selected =
      this.#getSelectedSwatch();

    if (!selected) return;

    await this.#selectVariant(
      selected
    );
  };

  #handlePointerOver = (event) => {
    const target =
      event.target instanceof Element
        ? event.target
        : null;

    if (!target) return;

    const label =
      target.closest('.variant-option__button-label') ||
      target.closest('label');

    if (!label) return;

    const related = event.relatedTarget;

    if (
      related instanceof Node &&
      label.contains(related)
    ) {
      return;
    }

    this.#previewVariant({
      target: label
    });
  };

 #handlePointerOut = (event) => {
  const target =
    event.target instanceof Element
      ? event.target
      : null;

  if (!target) return;

  const label =
    target.closest('.variant-option__button-label') ||
    target.closest('label');

  if (!label) return;

  const related = event.relatedTarget;

  if (
    related instanceof Node &&
    label.contains(related)
  ) {
    return;
  }

  const nextLabel =
    related instanceof Element
      ? (
          related.closest('.variant-option__button-label') ||
          related.closest('label')
        )
      : null;

  if (nextLabel) {
    this.#previewVariant({
      target: nextLabel
    });
    return;
  }

  this.#resetVariant();
 };

#handleImageHoverEnter = () => {
  const slideshow = this.#getSlideshow();
  if (!slideshow) return;

  const overlay =
    slideshow.querySelector('.cc-hover-second-image');

  const image =
    overlay?.querySelector('img');

  if (!image?.src) return;

  slideshow.classList.add(
    'cc-image-hover-active'
  );
};

#handleImageHoverLeave = () => {
  const slideshow = this.#getSlideshow();
  if (!slideshow) return;

  slideshow.classList.remove(
    'cc-image-hover-active'
  );
};

#setupImageHover() {
  const slideshow = this.#getSlideshow();
  if (!slideshow) return;

  slideshow.classList.add(
    'cc-image-fade-container'
  );

  const firstImage =
    this.#getFirstImage();

  if (firstImage) {
    firstImage.classList.add(
      'cc-first-image'
    );
  }

  this.#refreshHoverImage();

  if (
    slideshow.dataset.ccImageHoverReady ===
    'true'
  ) {
    return;
  }

  slideshow.addEventListener(
    'pointerenter',
    this.#handleImageHoverEnter
  );

  slideshow.addEventListener(
    'pointerleave',
    this.#handleImageHoverLeave
  );

  slideshow.addEventListener(
    'pointerdown',
    event => {
      const arrow =
        event.target instanceof Element
          ? event.target.closest(
              'slideshow-arrows button, slideshow-arrows'
            )
          : null;

      if (!arrow) return;

      slideshow.classList.remove(
        'cc-image-hover-active'
      );
    },
    true
  );

  slideshow.dataset.ccImageHoverReady =
    'true';
}

  #initializeVariantLogic() {
    this.#saveCurrentImageState();

    const link =
      this.refs?.productCardLink ||
      this.querySelector(
        'a[href*="/products/"]'
      );

    if (link) {
      this.#originalProductUrl =
        link.getAttribute(
          'href'
        ) || '';

      this.#selectedProductUrl =
        this.#originalProductUrl;
    }

    const title =
      this.refs?.productTitle ||
      this.querySelector(
        '.product-card__title'
      ) ||
      this.querySelector(
        '[data-product-title]'
      );

    if (title) {
      this.#originalProductTitle =
        title.textContent.trim();

      this.#selectedProductTitle =
        this.#originalProductTitle;
    }

    const selected =
      this.#getSelectedSwatch();

    if (selected) {
      const label =
        this.#getLabelFromInput(
          selected
        );

      const connectedUrl =
        this.#getConnectedUrl(
          selected,
          label
        );

      if (connectedUrl) {
        this.#currentImageMode =
          'connected';
      } else {
        this.#currentImageMode =
          'variant';
      }
    }

    this.addEventListener(
      'click',
      this.#handleSwatchClick
    );

    this.addEventListener(
      StandardEvents.productSelect,
      this.#handleProductSelect
    );

    this.addEventListener(
      'pointerover',
      this.#handlePointerOver
    );

    this.addEventListener(
      'pointerout',
      this.#handlePointerOut
    );

    requestAnimationFrame(
      () => {
        this.#setupImageHover();
      }
    );
  };
  // part-4
  connectedCallback() {
    super.connectedCallback();

    this.#initializeVariantLogic();

    const selected =
      this.#getSelectedSwatch();

    if (selected) {
      const label =
        this.#getLabelFromInput(
          selected
        );

      const connectedUrl =
        this.#getConnectedUrl(
          selected,
          label
        );

      const connectedVariantId =
        this.#getConnectedVariantId(
          selected,
          label
        );

      if (connectedUrl) {
        this.#loadConnectedVariant(
          connectedUrl,
          connectedVariantId
        );
      } else {
        this.#showSelectedVariantImage(
          selected,
          label
        );
      }
    }

    requestAnimationFrame(
      () => {
        this.#setupImageHover();
      }
    );

    requestAnimationFrame(
      () => {
        requestAnimationFrame(
          () => {
            this.#setupImageHover();
          }
        );
      }
    );
  }

  disconnectedCallback() {
    this.#hoverRequestToken++;

    this.#connectedRequestController?.abort();

    const slideshow =
      this.#getSlideshow();

    if (slideshow) {
      slideshow.removeEventListener(
        'pointerenter',
        this.#handleImageHoverEnter
      );

      slideshow.removeEventListener(
        'pointerleave',
        this.#handleImageHoverLeave
      );

      slideshow.classList.remove(
        'cc-image-hover-active'
      );

      const overlay =
        slideshow.querySelector(
          '.cc-hover-second-image'
        );

      overlay?.remove();

      delete slideshow.dataset
        .ccImageHoverReady;
    }

    this.removeEventListener(
      'click',
      this.#handleSwatchClick
    );

    this.removeEventListener(
      StandardEvents.productSelect,
      this.#handleProductSelect
    );

    this.removeEventListener(
      'pointerover',
      this.#handlePointerOver
    );

    this.removeEventListener(
      'pointerout',
      this.#handlePointerOut
    );

    super.disconnectedCallback();
  }
}

if (
  !customElements.get(
    'product-card'
  )
) {
  customElements.define(
    'product-card',
    ProductCard
  );
}

class SwatchesVariantPickerComponent
  extends VariantPicker {

  connectedCallback() {
    super.connectedCallback();

    this.parentProductCard =
      this.closest(
        'product-card'
      );
  }

  variantChanged(event) {
    const input =
      event?.target;

    if (
      input instanceof
      HTMLInputElement
    ) {
      input.checked =
        true;

      input.setAttribute(
        'aria-checked',
        'true'
      );

      input.setAttribute(
        'data-current-checked',
        'true'
      );
    }

    if (
      this.parentProductCard
    ) {
      const selected =
        this.querySelector(
          'input[type="radio"]:checked'
        );

      if (selected) {
        this.parentProductCard
          .dispatchEvent(
            new CustomEvent(
              StandardEvents.productSelect,
              {
                bubbles: true,
                detail: {
                  input: selected
                }
              }
            )
          );
      }
    }
  }

  showAllSwatches(event) {
    event?.preventDefault();
    event?.stopPropagation();

    this.setAttribute(
      'data-expanded',
      'true'
    );

    const moreSlot =
      this.querySelector(
        '[slot="more"], .variant-option__more-slot'
      );

    if (moreSlot) {
      moreSlot.style.display =
        'none';
    }

    const overflowList =
      this.refs?.overflowList;

    if (
      overflowList instanceof
      OverflowList
    ) {
      overflowList.showAll?.();
    }
  }
}

if (
  !customElements.get(
    'swatches-variant-picker-component'
  )
) {
  customElements.define(
    'swatches-variant-picker-component',
    SwatchesVariantPickerComponent
  );
}